import React from "react";
import { useTranslation, useI18next, Link } from "gatsby-plugin-react-i18next";
import { useBreakpoint } from "gatsby-plugin-breakpoints";
import PrimaryButton from "components/Design/Button/PrimaryButton";
import NavHamburger from "components/Icons/NavHamburger";
import useDrawer from "hooks/useDrawer";
import NavBarMobileOpen from "./NavBarMobileOpen/NavBarMobileOpen";
import DestinationCard from "../Card/DestinationCard";
import RoomSelectionSearchDrawer from "components/Design/Drawer/RoomSelectionSearchDrawer";
import DESTINATION from "dictionaries/destinations";
import { embedLanguageInUrl } from "utils/embedLanguageInUrl";
import useQueryParams from "hooks/useQueryParams";
import { toBool } from "utils/toBool";
import ConditionalLink from "components/ConditionalLink";

const NavMobile = ({ logo, open, logoLink }) => (
  <div className="flex items-center px-20 xl:py-20 justify-between py-12">
    <ConditionalLink sameTab={true} link={logoLink}>
      {logo}
    </ConditionalLink>
    <div onClick={open} className="cursor-pointer">
      <NavHamburger className="text-primary" />
    </div>
  </div>
);

const NavBar = ({
  logo,
  destinationCards,
  menuList,
  children,
  bookingEngineLink,
  cartCount,
  moreMenuListItem,
  contactOnDesktop,
  navbarMobileLogo,
  logoLink,
  whatsappLink,
  whatsappContact,
  restaurant,
  location,
  destination,
  hotelCode,
  minimumStay,
  bookingCode,
  stayPeriod,
  isPackage,
  hotel,
  packageCode,
  hotelId,
  roomId,
  minLeadDay,
}) => {
  const { t } = useTranslation("homepage");
  const breakpoints = useBreakpoint();
  const { open_booking_widget } = useQueryParams();
  const { language } = useI18next();

  const destinations = destinationCards.map((destination, id) => {
    const destinationLink = DESTINATION[destination.title];
    const link = destinationLink.startsWith("https://")
      ? embedLanguageInUrl(destinationLink, language)
      : destinationLink;
    let returnData = {
      key: destination.id,
      name: destination.name,
      title: t(destination.title),
      to: link,
      image: !breakpoints.lg ? destination?.mobile : destination?.desktop,
    };

    return <DestinationCard {...returnData} height="lg:h-200" />;
  });

  const { isOpen, open, close } = useDrawer();

  return (
    <>
      {/* Desktop Nav */}
      <div className="hidden xl:block">
        <div className="px-30 flex content-center font-mulish uppercase py-16">
          <ConditionalLink sameTab={true} link={logoLink}>
            {logo}
          </ConditionalLink>
          <div className="flex items-center text-12 ml-auto space-x-30 mr-30">
            {children}
          </div>
          <div className="flex items-center">
            {contactOnDesktop ? (
              <Link to={contactOnDesktop?.url}>
                <PrimaryButton
                  label={contactOnDesktop?.label || t("Contact Us")}
                />
              </Link>
            ) : (
              <RoomSelectionSearchDrawer
                packageCode={packageCode}
                defaultOpen={toBool(open_booking_widget)}
                isPackage={isPackage}
                stayPeriod={stayPeriod}
                minimumStay={minimumStay}
                minLeadDay={minLeadDay}
                bookingCode={bookingCode}
                hotelCode={hotelCode}
                location={location}
                hotelId={hotelId}
                roomId={roomId}
                cta={(onClick) => (
                  <PrimaryButton label={t("View Rates")} onClick={onClick} />
                )}
              />
            )}
          </div>
        </div>
      </div>

      {/* Mobile Nav */}
      <div className="xl:hidden">
        <NavMobile logo={logo} open={open} logoLink={logoLink} />
        <NavBarMobileOpen
          visible={isOpen}
          close={close}
          destinationCards={destinations}
          menuList={menuList}
          bookingEngineLink={bookingEngineLink}
          cartCount={cartCount}
          moreItems={moreMenuListItem}
          contactOnDesktop={contactOnDesktop}
          logo={navbarMobileLogo}
          logoLink={logoLink}
          whatsappContact={whatsappContact}
          whatsappLink={whatsappLink}
          restaurant={restaurant}
          location={location}
          destination={destination}
          hotelCode={hotelCode}
          minimumStay={minimumStay}
          minLeadDay={minLeadDay}
          bookingCode={bookingCode}
          stayPeriod={stayPeriod}
          isPackage={isPackage}
          hotel={hotel}
          packageCode={packageCode}
          hotelId={hotelId}
          roomId={roomId}
        />
      </div>
    </>
  );
};

export default NavBar;
